import React, { useState } from 'react'
import { Tooltip, OverlayTrigger } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { trans } from 'sharedKernel/i18n'
import { EVALUATION_STATUS, SOLUTION_STATUS } from 'sharedKernel/constants'
import { HubTable } from 'components/HubDefaultTable'
import { IEvaluation, ISolution } from '../contracts'
import { EvaluationLabel, CenterContent, CellLink, IdeaTitle } from './style'
import { filterByTabValue, filterValueMatchAnyField, sortRows } from './behavior'
import { NoContent } from 'components/NoContent'
import NoContentImg from 'assets/images/defaults/no_challenges.png'
import { ApproveRejectButtons } from 'components/ApproveRejectButtons'
import { TitleContainer } from 'components/HubDefaultTable/style'
import { showModal } from 'components/popup'
import { EvaluationModal } from '../evaluationModal'
import { useParams } from 'react-router'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { AnimatedSortButtons } from 'components/HubDefaultTable/OrderByButton'

export interface HubCompanyChallengeSolutionsTableProps {
  solutions: Solutions
  tabStatus: string
  filterValue: string
  approveSolution: (ideaEvaluationStepId: string) => void
  archiveSolution: (ideaEvaluationStepId: string) => void
  evaluationsSummary?: any
  stepData?: any
}

interface Solutions {
  rows: any[]
}

export interface SortTable {
  finalScoreAsc: boolean
  statusAsc: boolean
  solutionTitleAsc: boolean
}

export interface OrderClick {
  finalScoreClick: boolean
  statusClick: boolean
  solutionTitleClick: boolean
}

export const EVALUATORS_SHOWN_LIST = 5

export const NO_CONTENT_CHALLENGE_SOLUTIONS_MESSAGE = 'Nenhuma solução encontrada para este evento'

export const HubCompanyChallengeSolutionsTable = ({ solutions, tabStatus, filterValue, approveSolution, archiveSolution, evaluationsSummary, stepData }: HubCompanyChallengeSolutionsTableProps): JSX.Element => {
  const { challengeId } = useParams()
  const navigate = useNavigate()
  const [sortTable, setSortTable] = useState<SortTable>({ finalScoreAsc: false, statusAsc: false, solutionTitleAsc: false })
  const [orderClick, setOrderClick] = useState<OrderClick>({ finalScoreClick: false, statusClick: false, solutionTitleClick: true })
  const labels = {
    name: trans('Solução'),
    evaluation: trans('Avaliação'),
    grade: trans('Nota Parcial'),
    status: trans('Situação'),
    actions: trans('Ações'),
    noContent: trans(NO_CONTENT_CHALLENGE_SOLUTIONS_MESSAGE),
    canceledEvaluationTooltip: trans('Cancelada'),
    emptyEvaluationTooltip: trans('Sem avaliação'),
    evaluator: trans('Avaliação'),
    incompleteEvaluation: trans('Avaliação não finalizada'),
    incompleteEvaluations: trans('Avaliações não finalizadas'),
    notLaunched: trans('Esta solução ainda não foi publicada.'),
    alreadyEvaluated: trans('Esta solução já foi aprovada ou rejeitada.'),
    notEvaluated: trans('Esta solução ainda não foi avaliada.'),
    pending: trans('Pendente'),
    approved: trans('Aprovado'),
    disapproved: trans('Reprovado')
  }

  const columnMasks = {
    pendingEvaluation: trans('Não iniciado'),
    notAcceptedEvaluation: trans('Não aceito'),
    empty: '-'
  }

    const solutionStatusLabel = {
      APPROVED: { bgColor: '#d4edda', color: '#155724', label: labels.approved },
      REJECTED: { bgColor: '#f8d7da', color: '#721c24', label: labels.disapproved },
      CREATED: { bgColor: '#fff3cd', color: '#856404', label: labels.pending },
      LAUNCHED: { bgColor: '#fff3cd', color: '#856404', label: labels.pending }
    }


  const tableHeader = (): any[] => {
    return [
      getCenteredContent(buildHeaderSolutionTitle()),
      getCenteredContent(labels.evaluation),
      getCenteredContent(buildHeaderFinalScore()),
      getCenteredContent(buildHeaderStatus()),
      getCenteredContent(labels.actions)
    ]
  }
  const buildHeaderSolutionTitle = (): JSX.Element => {
    return (
      <>
        {labels.name}
        <div className='ml-1'>
          <AnimatedSortButtons orderByAsc={sortTable.solutionTitleAsc}
            onClick={() => {
              setSortTable({ ...sortTable, solutionTitleAsc: !sortTable.solutionTitleAsc })
              setOrderClick({ ...orderClick, solutionTitleClick: true, finalScoreClick: false, statusClick: false })
            }} />
        </div>
      </>
    )
  }

  const buildHeaderFinalScore = (): JSX.Element => {
    return (
      <>
        {labels.grade}
        <div className='ml-1'>
          <AnimatedSortButtons orderByAsc={sortTable.finalScoreAsc}
            onClick={() => {
              setSortTable({ ...sortTable, finalScoreAsc: !sortTable.finalScoreAsc })
              setOrderClick({ ...orderClick, finalScoreClick: true, solutionTitleClick: false, statusClick: false })
            }}
          />
        </div>
      </>
    )
  }
  const buildHeaderStatus = (): JSX.Element => {
    return (
      <>
        {labels.status}
        <div className='ml-1'>
          <AnimatedSortButtons orderByAsc={sortTable.statusAsc}
            onClick={() => {
              setSortTable({ ...sortTable, statusAsc: !sortTable.statusAsc })
              setOrderClick({ ...orderClick, statusClick: true, finalScoreClick: false, solutionTitleClick: false })
            }}
          />
        </div>
      </>
    )
  }

  const buildSolutionRows = (): any[] => {
    filterRows()
    return solutions.rows
      .filter((solution) => { return filterByTabValue(solution, tabStatus) })
      .filter((solution) => { return filterValueMatchAnyField(solution, filterValue, columnMasks) })
      .map((solution: any, index) => {
        return buildSolutionColumns(solution)
      })
  }

  const filterRows = (): void => {
    let newRows = sortRows(solutions.rows, sortTable.finalScoreAsc, sortTable.solutionTitleAsc, orderClick.finalScoreClick, orderClick.solutionTitleClick)
    
    const launchedIdeas = newRows.filter((solution) => normalizeStatus(solution.status) === SOLUTION_STATUS.LAUNCHED)
    const approvedIdeas = newRows.filter((solution) => normalizeStatus(solution.status) === SOLUTION_STATUS.APPROVED)
    const createdIdeas = newRows.filter((solution) => normalizeStatus(solution.status) === SOLUTION_STATUS.CREATED)
    const disapprovedIdeas = newRows.filter((solution) => normalizeStatus(solution.status) === SOLUTION_STATUS.DISAPPROVED)
    
    if (orderClick.statusClick) {
      if (sortTable.statusAsc) {
        newRows = [...disapprovedIdeas, ...createdIdeas, ...launchedIdeas, ...approvedIdeas]
      } else {
        newRows = [...approvedIdeas, ...launchedIdeas, ...createdIdeas, ...disapprovedIdeas]
      }
    }
    solutions.rows = [...newRows]
  }

  const buildSolutionColumns = (solution: any): any[] => {
    const solutionColumns: any = []

    solutionColumns.push(getSolutionTitle(solution))
    solutionColumns.push(getCenteredContent(getEvaluationPercentage(solution)))
    solutionColumns.push(getCenteredContent(solution.progress?.points))
    solutionColumns.push(getCenteredContent(getSolutionStatus(solution)))
    solutionColumns.push(getSolutionActions(solution))

    return solutionColumns
  }

  const calculateAverageScore = (solution: any): number => {
    if (solution.evaluation?.averageScore !== undefined) {
      return solution.evaluation.averageScore
    }

    if (solution.progress?.sections && Array.isArray(solution.progress.sections)) {
      const criteria = getCriteriaWithWeights()
      if (criteria.length === 0) return 0

      let totalScore = 0
      let totalWeight = 0

      solution.progress.sections.forEach((section: any) => {
        if (section.fields && Array.isArray(section.fields)) {
          section.fields.forEach((field: any) => {
            const criterion = criteria.find((c: any) => (c.id || c._id) === (field.id || field._id))
            if (criterion && field.points !== null && field.points !== undefined) {
              const weight = criterion.weight !== undefined && criterion.weight !== 0 ? criterion.weight : 1
              totalScore += field.points * weight
              totalWeight += weight
            }
          })
        }
      })

      return totalWeight > 0 ? totalScore / totalWeight : 0
    }

    if (!solution.evaluations || !Array.isArray(solution.evaluations) || solution.evaluations.length === 0) {
      return 0
    }

    const criteria = getCriteriaWithWeights()
    if (criteria.length === 0) return 0

    let totalScore = 0
    let totalWeight = 0

    solution.evaluations.forEach((evaluation: any) => {
      if (evaluation.status === EVALUATION_STATUS.EVALUATED && evaluation.answers) {
        evaluation.answers.forEach((answer: any) => {
          const criterion = criteria.find((c: any) => (c.id || c._id) === (answer.fieldId || answer.criterionId))
          if (criterion && answer.value !== null && answer.value !== undefined) {
            const weight = criterion.weight !== undefined && criterion.weight !== 0 ? criterion.weight : 1
            totalScore += answer.value * weight
            totalWeight += weight
          }
        })
      }
    })

    return totalWeight > 0 ? totalScore / totalWeight : 0
  }

  const getCriteriaWithWeights = (): any[] => {
    if (!stepData || !stepData.sections) return []
    const allFields: any[] = []
    stepData.sections.forEach((section: any) => {
      if (section.fields) {
        allFields.push(...section.fields)
      }
    })
    return allFields
  }

  const getEvaluationPercentage = (solution: any): string => {
    if (solution.progress?.fillProgress !== undefined && solution.progress?.fillProgress !== null) {
      const percentage = Math.round(solution.progress.fillProgress * 100)
      return `${percentage}% avaliado`
    }

    let evaluations = solution.evaluation?.evaluations || solution.evaluations || []
    
    if (!Array.isArray(evaluations)) {
      evaluations = []
    }

    const totalEvaluators = evaluations.length
    const completedEvaluations = evaluations.filter(
      (evaluation: any) => evaluation.status === EVALUATION_STATUS.EVALUATED
    ).length

    if (totalEvaluators === 0) return '0% avaliado'

    const percentage = Math.round((completedEvaluations / totalEvaluators) * 100)
    return `${percentage}% avaliado`
  }

  const getFormattedScore = (score: number): string => {
    return score.toFixed(2)
  }

  const getCellLink = (content: any, title: string, solutionId: string, evaluatorId: string): any => {
    return (
      <CellLink
        onClick={() => {
          showModal('single', {
            title: title,
            message: <EvaluationModal challengeId={challengeId} solutionId={solutionId} evaluatorId={evaluatorId} />
          })
        }}
      >
        {content}
      </CellLink>
    )
  }
  const getSolutionTitle = (solution: any): JSX.Element => {
    const title = solution.idea?.name || solution.title || solution.ideaTitle || solution.name || 'Sem título'
    const solutionId = solution.idea?.id || solution.id || solution.ideaId
    
    return (
      <TitleContainer className='d-flex justify-content-start'>
        <CellLink
          onClick={
            () => navigate(`/hub/company/challenge/${challengeId}/solutions/${solutionId}/subscription`)
          }
        >
          <IdeaTitle>
            {title}
          </IdeaTitle>
        </CellLink>
      </TitleContainer>
    )
  }
  const getEvaluationContent = (evaluation: IEvaluation): JSX.Element | number | string => {
    const evaluationStatusContentRule = {
      [`${EVALUATION_STATUS.CREATED}`]: getIncompleteEvaluation(evaluation.points),
      [`${EVALUATION_STATUS.PENDING}`]: getEvaluationLabel(columnMasks.pendingEvaluation, 'yellow'),
      [`${EVALUATION_STATUS.EVALUATED}`]: evaluation.points,
      [`${EVALUATION_STATUS.NOT_ACCEPTED}`]: getEvaluationLabel(columnMasks.notAcceptedEvaluation, 'red'),
      [`${EVALUATION_STATUS.CANCELED}`]: getEvaluationEmptyLabel(labels.canceledEvaluationTooltip)
    }

    return evaluationStatusContentRule[evaluation.status]
  }

  const getIncompleteEvaluation = (text: number): JSX.Element => {
    return (
      <>
        <OverlayTrigger overlay={<Tooltip>{labels.incompleteEvaluation}</Tooltip>}>
          <span>{text}<span style={{ color: 'red' }}>*</span></span>
        </OverlayTrigger>
      </>
    )
  }

  const getEvaluationLabel = (text: string, labelColor: string): JSX.Element => {
    return <EvaluationLabel color={labelColor}>{text}</EvaluationLabel>
  }

  const getEvaluationEmptyLabel = (text: string): JSX.Element => {
    return (
      <OverlayTrigger overlay={<Tooltip>{text}</Tooltip>}>
        <div>{columnMasks.empty}</div>
      </OverlayTrigger>
    )
  }

  const getSolutionStandartDeviation = (solution: ISolution): JSX.Element => {
    return (
      <>
        {solution.evaluation.standardDeviation}
      </>
    )
  }

  const normalizeStatus = (status: any): string =>  status? status.toUpperCase() : 'CREATED'

  const getSolutionStatus = (solution: any): JSX.Element => {

    const statusName = normalizeStatus(solution.status)
    
    const statusInfo = solutionStatusLabel[statusName] || solutionStatusLabel[SOLUTION_STATUS.CREATED]
    return (
      <div
        style={{
          padding: '4px 12px',
          borderRadius: '4px',
          backgroundColor: statusInfo?.bgColor,
          color: statusInfo?.color,
          fontWeight: 500,
          display: 'inline-block'
        }}
      >
        {statusInfo?.label}
      </div>
    )
  }

  const getSolutionActions = (solution: any): JSX.Element => {
    const statusName = normalizeStatus(solution.status);
    
    
    const getTippyMessage = (): string => {
      return canExecuteAction()? "": labels.alreadyEvaluated
    }

    const getIdeaEvaluationStepId = (): string => {
      if (solution.ideaEvaluationStepId) {
        return solution.ideaEvaluationStepId
      }
      
      if (evaluationsSummary && Array.isArray(evaluationsSummary)) {
        const ideaId = solution.idea?.id || solution.ideaId
        const summaryItem = evaluationsSummary.find((item: any) => item.ideaId === ideaId)
        return summaryItem?.id || solution.id || solution.ideaId
      }
      return solution.id || solution.ideaId
    }

    const canExecuteAction  =  (): boolean => {
      return statusName === normalizeStatus(SOLUTION_STATUS.LAUNCHED)  ||  
        statusName === normalizeStatus(SOLUTION_STATUS.CREATED)
    }

    return (
      <CenterContent>
        <ApproveRejectButtons
          positiveAction={() => approveSolution(getIdeaEvaluationStepId())}
          negativeAction={() => archiveSolution(getIdeaEvaluationStepId())}
          disabled={!canExecuteAction()}
          tipyContent={getTippyMessage()}
        />
      </CenterContent>
    )
  }

  const getCenteredContent = (content: any): any => {
    return (
      <CenterContent>
        {content}
      </CenterContent>
    )
  }
  const getTableCaption = (): JSX.Element => {
    return (
      <span><span style={{ color: 'red' }}>*</span> {labels.incompleteEvaluations}</span>
    )
  }

  return (
    <>
      {
        solutions === null || solutions === undefined || solutions.rows.length === 0
          ? <NoContent text={labels.noContent} image={NoContentImg} />
          : <HubTable
            header={tableHeader()}
            rows={buildSolutionRows()}
            caption={getTableCaption()}
          />
      }
    </>
  )
}
